import type { FC } from 'react'

import { LoaderWrapper, StyledText, Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { TrButton } from '@skytech/ui/Table/TrButton'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { LoadingError } from '@/components/LoadingError'
import { useSortParams } from '@/hooks/searchParams'
import { useFormattedNumber } from '@/hooks/use-formatted-number'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'
import { BreadCrumbs, Crumb } from '@/modules/admin/drilldown/BreadCrumbs'
import { DrillDownOptions } from '@/modules/admin/drilldown/DrillDownOptions'
import { getArraySumForSameCurrency } from '@/modules/admin/drilldown/helpers'
import { NoResultsWrapper } from '@/modules/admin/drilldown/NoResultsWrapper'
import queries from '@/modules/admin/queries'
import type { DrillDownParams, DrillDownProductCategory } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import type { DrillDownType } from '@/modules/admin/resources/drill-down/types'
import { APP_ROUTES } from '@/util/app-routes'

import { setProductCategoryIdAction } from '../context/actions.context'
import { useDrillDownContext } from '../context/index.context'
import { extractCurrencies } from '../utils'

export const ServiceReport: FC = () => {
  const { locale } = useAppContext()
  const { state } = useDrillDownContext()

  const params: DrillDownParams = {
    type: state.type as DrillDownType,
    typeId: String(state.typeId),
    year: String(state.year),
    period: String(state.period),
  }

  const { data, isError } = useQuery(queries.adminDrillDownCategories(params), () =>
    adminResources.getDrillDownCategories(params)
  )

  const stringMatcher = (item: DrillDownProductCategory) => [
    item.productCategoryName,
    item.amount,
    item.salaryDeductionAmount,
  ]

  const rowsFiltered =
    data?.rows?.filter(row => {
      const isCurrencyMatch = row.currency === state.currency
      const isAmountNotZero = row.amount !== 0
      const isSalaryDeductionAmountNotZero = row.salaryDeductionAmount !== 0

      const isQueryFilterMet = getQueryFilter(state.search, stringMatcher)(row)
      return (
        isCurrencyMatch && (isAmountNotZero || isSalaryDeductionAmountNotZero) && isQueryFilterMet
      )
    }) || []

  const [{ querySort, querySortBy }, { handleSort }] = useSortParams<
    keyof DrillDownProductCategory
  >('desc', 'amount')

  const sortedList = getSortedList({
    list: rowsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList || [], 30)

  if (isError) return <LoadingError />
  if (!data) return <LoaderWrapper isLoading />

  const currencies = extractCurrencies(data.rows)

  const totalAmount: number = getArraySumForSameCurrency(data?.rows, 'amount', state.currency)
  const totalSalaryDeductionAmount: number = getArraySumForSameCurrency(
    data?.rows,
    'salaryDeductionAmount',
    state.currency
  )

  return (
    <div>
      <BreadCrumbs>
        <Crumb
          name={data.entity.name}
          route={APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE}
          params={params}
        />
      </BreadCrumbs>
      <DrillDownOptions
        data={sortedList.map(item => ({
          'product category': item.productCategoryName,
          amount: item.amount,
          'salary deduction': item.salaryDeductionAmount,
          'part of cost': item.amount / totalAmount,
        }))}
        columnFormats={{
          amount: 'amount',
          'salary deduction': 'amount',
          'part of cost': 'percentage',
        }}
        title="Report"
        currencies={currencies}
        hasSearch
      />
      <NoResultsWrapper numOfResults={data.rows.length}>
        <Table>
          <thead>
            <tr>
              <Th id="productCategoryName" className="px-4" onClick={handleSort}>
                <Translation id="product category" />
              </Th>
              <Th id="amount" className="px-4 text-right" onClick={handleSort}>
                <Translation id="amount" />
              </Th>
              <Th id="salaryDeductionAmount" className="px-4 text-right" onClick={handleSort}>
                <Translation id="salary deduction" />
              </Th>
              <th id="" className="px-4 text-right">
                <Translation id="part of cost" />
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map((product: DrillDownProductCategory) => (
              <Row key={product.productCategoryId} totalAmount={totalAmount} {...product} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="px-4">
                <StyledText>
                  <Translation id="total" />
                </StyledText>
              </td>
              <td className="px-4 text-right">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
              <td className="px-4 text-right">
                <StyledText>
                  <FormattedNumberWithCurrency
                    amount={totalSalaryDeductionAmount}
                    currency={state.currency}
                    locale={rowsFiltered?.[0]?.locale}
                  />
                </StyledText>
              </td>
            </tr>
          </tfoot>
        </Table>
        <ListLoaderComponent />
      </NoResultsWrapper>
    </div>
  )
}

const Row: FC<DrillDownProductCategory> = ({
  productCategoryId,
  productCategoryName,
  amount,
  salaryDeductionAmount,
  totalAmount = 0,
  currency,
  locale,
}) => {
  const navigate = useNavigate()

  const { state, dispatch } = useDrillDownContext()
  const { formatNumber: formatNumberWithCurrency } = useFormattedNumberWithCurrency()
  const { formatNumber } = useFormattedNumber()

  const onClick = () => {
    const search = new URLSearchParams({
      type: state.type as DrillDownType,
      typeId: String(state.typeId),
      year: String(state.year),
      period: String(state.period),
      productCategoryId: String(productCategoryId),
      currency: String(state.currency),
    }).toString()

    const pathname = APP_ROUTES.REPORTS_DRILL_DOWN_SERVICE_CATEGORY

    dispatch(setProductCategoryIdAction(String(productCategoryId)))

    navigate({ pathname, search })
  }

  return (
    <TrButton onClick={onClick}>
      <Td>
        <Highlight pattern={state.search} text={productCategoryName} />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        <Highlight
          pattern={state.search}
          text={formatNumberWithCurrency({
            amount: salaryDeductionAmount,
            currency,
            locale,
          })}
        />
      </Td>
      <Td className="text-right">
        {amount > 0 && (
          <>
            <Highlight
              pattern={state.search}
              text={formatNumber({
                amount: (amount / totalAmount) * 100,
                locale,
              })}
            />
            %
          </>
        )}
      </Td>
    </TrButton>
  )
}
